import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Credentials } from 'src/app/core/models/Credentials';
import { environment } from 'src/environments/environment';
import { Auth } from 'src/app/core/models/UserSession';
import { HttpClientWrapperService, Params } from '../http/http-client-wrapper.service';
import {
  BehaviorSubject,
  catchError,
  finalize,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError
} from 'rxjs';
import * as Sentry from '@sentry/angular';
import { User, SignUpUser } from '../../models/User';
import { TabService } from '../navigation/tab.service';
import { GuestUserService } from '../user/guest-user.service';
import { GoogleAuthResponse } from './google-auth.service';
import { LoggerService } from '../logger/logger.service';
import { PushNotificationService } from '../push/push-notification.service';

export interface AuthMeResponse {
  userId: number;
  username: string;
  email: string;
  isAdmin: boolean;
  authorities: string[];
  hasPassword: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly authApiUrl = `${environment.beApiUrl}/api/v1/auth`;
  private readonly userApiUrl = `${environment.beApiUrl}/api/v1/user-profile`;
  private httpClient = inject(HttpClientWrapperService);
  private logger = inject(LoggerService);
  private readonly pushNotificationService = inject(PushNotificationService);

  public userSession$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  private readonly _currentUser = signal<User | null>(null);
  private readonly _hydrated = signal<boolean>(false);
  private readonly _hasPassword = signal<boolean>(false);

  readonly currentUser = this._currentUser.asReadonly();
  readonly hasPassword = this._hasPassword.asReadonly();

  /** @deprecated Use AuthFacade.principal() — see PR #621 for migration plan. */
  readonly user$: Observable<User | null> = toObservable(this._currentUser);

  authenticated = false;

  constructor(
    private tabService: TabService,
    private guestUserService: GuestUserService
  ) {
    this.initializeAuth();
  }

  private initializeAuth() {
    const accessToken: string | null = sessionStorage.getItem('accessToken');
    const userId: string | null = sessionStorage.getItem('userId');

    if (accessToken && userId && userId !== 'null' && userId !== 'undefined') {
      this.authenticated = true;
      this.userSession$.next(accessToken);

      this.httpClient.get<AuthMeResponse>(`${this.authApiUrl}/me`).pipe(
        tap((authUser) => {
          this._hasPassword.set(authUser.hasPassword);
        }),
        switchMap(() => this.getUserById(parseInt(userId, 10)))
      ).subscribe({
        error: (error) => {
          if (error?.status === 401) {
            this.logger.info('Session expired during auth init', { action: 'auth_init' });
          } else {
            this.logger.error('Error initializing with user', error, { action: 'auth_init' });
          }
          this.logout();
        },
      });
    } else {
      this.logout();
    }
  }

  getUserById(userId: number): Observable<User> {
    if (!userId || isNaN(userId)) {
      this.logger.error('Invalid userId provided to getUserById', undefined, { userId, action: 'get_user' });
      return throwError(() => new Error('Invalid userId'));
    }
    
    return this.httpClient.get<User>(`${this.userApiUrl}?id=${userId}`).pipe(
      tap((usr: User) => {
        this.authenticated = true;
        this._currentUser.set(usr);
        Sentry.setUser({ email: usr.email });
      }),
      catchError((error) => {
        this.logger.error('Error fetching user details', error, { action: 'get_user' });
        return throwError(() => error);
      }),
      finalize(() => {
        this._hydrated.set(true);
      })
    );
  }

  get session$(): Observable<string | null> {
    return this.userSession$.asObservable();
  }
  
  readonly isHydrated$: Observable<boolean> = toObservable(this._hydrated);

  setUser(user: User): void {
    this._currentUser.set(user);
  }

  clearUserState(): void {
    this._currentUser.set(null);
    this.authenticated = false;
    this.userSession$.next(null);
  }

  // Called by HttpClientWrapperService on a session-invalidating 401.
  // Tokens are cleared SYNCHRONOUSLY first to close the re-login race:
  // the wrapper navigates to /login immediately and a fast user could
  // complete a new login while an awaited push unsubscribe was still in
  // flight — the eventual cleanup would then wipe the new session token.
  // Trade-off: the backend push unsubscribe call below will 401 without
  // a bearer token, leaving a stale subscription server-side. Acceptable
  // because the next login re-establishes the subscription idempotently.
  async handleSessionExpiry(): Promise<void> {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('username');
    sessionStorage.removeItem('userId');
    // Org context must go too: orgContextGuard treats these keys as an active,
    // already-validated context and would carry them into the next login.
    sessionStorage.removeItem('currentCharityId');
    sessionStorage.removeItem('currentBusinessId');
    this.clearUserState();
    await this.pushNotificationService.unsubscribeAll().catch((error) => {
      this.logger.error('Push unsubscribe failed during session expiry', error, { action: 'session_expiry' });
    });
  }

  login(credentials: Credentials): Observable<void> {
    return this.httpClient
      .postWithoutAuth<Auth, Credentials>(`${this.authApiUrl}/login`, credentials)
      .pipe(
        tap((auth: Auth) => {
          // WHY: a successful login response always carries an access token.
          const accessToken: string = auth.accessToken!;
          sessionStorage.setItem('accessToken', accessToken);
          this.userSession$.next(accessToken);
          this.authenticated = true;
          this.guestUserService.clearLocalGuestSession();
        }),
        switchMap(() =>
          this.httpClient.get<AuthMeResponse>(`${this.authApiUrl}/me`)
        ),
        tap((authUser) => {
          sessionStorage.setItem('userId', authUser.userId.toString());
          sessionStorage.setItem('username', authUser.username);
          this._hasPassword.set(authUser.hasPassword);
        }),
        switchMap((authUser) => 
          this.httpClient.get<User>(`${this.userApiUrl}?id=${authUser.userId}`)
        ),
        tap((user: User) => {
          this._currentUser.set(user);
          Sentry.setUser({ email: user.email });
        }),
        map((): void => void 0),
        catchError((error) => {
          if ((error as { status?: number })?.status !== 401) {
            this.logger.error('Login error', error, { action: 'login' });
          }
          return throwError(() => new Error('Login failed. Please try again.'));
        })
      );
  }

  async logout(): Promise<void> {
    const username: string | null = sessionStorage.getItem('username');

    // Unsubscribe from push notifications BEFORE clearing the token —
    // the backend call requires the bearer token that cleanup() removes.
    await this.pushNotificationService.unsubscribeAll().catch((error) => {
      this.logger.error('Push unsubscribe failed during logout', error, { action: 'logout' });
    });

    const cleanup: () => void = () => {
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('username');
      sessionStorage.removeItem('userId');
      sessionStorage.removeItem('currentCharityId');
      sessionStorage.removeItem('currentBusinessId');
      this.authenticated = false;
      this.tabService.clearAllHistory();
      this._hydrated.set(true);
      this.userSession$.next(null);
      this._currentUser.set(null);
      this._hasPassword.set(false);
    };

    if (username !== null) {
      return this.httpClient
        .post<void, Params>(`${this.authApiUrl}/logout`, { username })
        .pipe(
          catchError(() => of(void 0)),
          finalize(() => {
            cleanup();
          })
        )
        .toPromise();
    } else {
      cleanup();
    }
  }

  changePassword(payload: { oldPassword?: string; newPassword: string }): Observable<void> {
    const body: { oldPassword?: string; newPassword: string } = payload.oldPassword
      ? { oldPassword: payload.oldPassword, newPassword: payload.newPassword }
      : { newPassword: payload.newPassword };

    return this.httpClient
      .post<void, typeof body>(`${this.authApiUrl}/change-password`, body)
      .pipe(
        switchMap(() => from(this.logout())),
        map((): void => void 0),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  signUpNewUser(password: string, user: User): Observable<User> {
    return this.httpClient
      .postWithoutAuth<User, SignUpUser>(`${this.userApiUrl}/create-user`, {
        profile: user,
        password,
      })
      .pipe(
        tap(() => {
          this.guestUserService.clearLocalGuestSession()
        }),
        catchError((error) => {
          this.logger.error('Signup error', error, { action: 'signup' });
          return throwError(() => error);
        })
      );
  }

  convertGuestUser(password: string, user: User, guestId: string): Observable<User> {
    return this.httpClient
      .postGuest<User, SignUpUser>(`${this.userApiUrl}/convert-from-guest/${guestId}`, {
        profile: user,
        password,
      })
      .pipe(
        tap(() => {
          this.guestUserService.clearLocalGuestSession()
        }),
        catchError((error) => {
          this.logger.error('Guest conversion error', error, { action: 'convert_guest' });
          return throwError(() => new Error('Signup failed. Please try again.'));
        })
      );
  }

  forgotPassword(username: string): Observable<void> {
    return this.httpClient.postWithoutAuth<void, Params>(`${this.authApiUrl}/forgot-password`, {
      username,
    });
  }

  resetPassword(resetToken: string, newPassword: string): Observable<void> {
    return this.httpClient.postWithoutAuth<void, Params>(`${this.authApiUrl}/reset-password`, {
      newPassword,
      resetToken,
    });
  }

  verifyEmail(userId: number, token: string): Observable<void> {
    return this.httpClient.postWithoutAuth<void, Params>(`${this.authApiUrl}/verify-email`, {
      relatedId: userId,
      token,
    });
  }

  checkUsernameAvailability(username: string): Observable<boolean> {
    return this.httpClient.getWithoutAuth<{ available: boolean }>(
      `${this.authApiUrl}/availability?username=${encodeURIComponent(username)}`
    ).pipe(
      map((response) => response.available),
      catchError((error) => {
        this.logger.error('Error checking username availability', error, { action: 'check_username' });
        return throwError(() => error);
      })
    );
  }

  changeProfilePic(id: number, image: File): Observable<User> {
    const formData: FormData = new FormData();
    formData.append('image', image);

    return this.httpClient.post<User, FormData>(`${this.userApiUrl}/${id}/upload-image`, formData);
  }

  getUserAthority() {
    return this.httpClient.get<Auth>(`${this.authApiUrl}/me`);
  }

  handleGoogleAuthSuccess(response: GoogleAuthResponse): Observable<void> {
    // WHY: handleGoogleAuthSuccess runs only on the authenticated branch, where the
    // backend returns an access token (the needs-completion branch carries a
    // temporaryToken instead — see SUN-878).
    const accessToken: string = response.accessToken!;
    sessionStorage.setItem('accessToken', accessToken);
    this.userSession$.next(accessToken);

    return this.httpClient.get<AuthMeResponse>(`${this.authApiUrl}/me`).pipe(
      tap((authUser) => {
        sessionStorage.setItem('userId', authUser.userId.toString());
        sessionStorage.setItem('username', authUser.username);
        this._hasPassword.set(authUser.hasPassword);
      }),
      switchMap((authUser) =>
        this.httpClient.get<User>(`${this.userApiUrl}?id=${authUser.userId}`)
      ),
      tap((user: User) => {
        this.authenticated = true;
        this.guestUserService.clearLocalGuestSession();
        this._currentUser.set(user);
        Sentry.setUser({ email: user.email });
      }),
      map((): void => void 0),
      catchError((error) => {
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('userId');
        sessionStorage.removeItem('username');
        this.authenticated = false;
        this.userSession$.next(null);
        this.logger.error('Google auth session error', error, { action: 'google_auth' });
        return throwError(() => new Error('Failed to establish session. Please try again.'));
      })
    );
  }

  refreshCurrentUser() {
    const userId: string | null = sessionStorage.getItem('userId');
    if (userId && userId !== 'null' && userId !== 'undefined') {
      this.getUserById(parseInt(userId, 10)).subscribe({
        error: (error) => {
          this.logger.error('Error refreshing user', error, { action: 'refresh_user' });
          this.logout();
        },
      });
    } else {
      this.logger.warn('Cannot refresh user: invalid userId', { action: 'refresh_user' });
      this.logout();
    }
  }
}
